import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ChangePasswordForm,
  changePasswordFormSchema,
  type DeleteAccountRequest,
  deleteAccountRequestSchema,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Segmented } from '../components/ui/segmented';
import { Card, CardTitle } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { getThemeChoice, setThemeChoice, type ThemeChoice } from '../lib/theme';

function ThemeCard() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<ThemeChoice>(getThemeChoice);
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>{t('account.theme')}</CardTitle>
      <Segmented
        label={t('account.theme')}
        options={[
          { value: 'system', label: t('account.themeSystem') },
          { value: 'light', label: t('account.themeLight') },
          { value: 'dark', label: t('account.themeDark') },
        ]}
        value={theme}
        onChange={(choice) => {
          setTheme(choice);
          setThemeChoice(choice);
        }}
      />
    </Card>
  );
}

function ChangePasswordCard() {
  const { t } = useTranslation();
  const form = useForm<ChangePasswordForm>({ resolver: zodResolver(changePasswordFormSchema) });
  const change = useMutation({
    mutationFn: ({ currentPassword, newPassword }: ChangePasswordForm) =>
      api.auth.changePassword({ currentPassword, newPassword }),
    onSuccess: () => form.reset({ currentPassword: '', newPassword: '', confirmPassword: '' }),
  });
  const errors = form.formState.errors;
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>{t('account.passwordTitle')}</CardTitle>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={form.handleSubmit((v) => change.mutate(v))}
      >
        <FormError error={change.error} />
        {change.isSuccess && (
          <p role="status" className="text-sm text-fg">
            {t('account.passwordChanged')}
          </p>
        )}
        <Field label={t('account.passwordCurrent')} error={errors.currentPassword} kind="password">
          <Input
            type="password"
            autoComplete="current-password"
            {...form.register('currentPassword')}
          />
        </Field>
        <Field
          label={t('account.passwordNew')}
          hint={t('fields.passwordHint')}
          error={errors.newPassword}
          kind="password"
        >
          <Input type="password" autoComplete="new-password" {...form.register('newPassword')} />
        </Field>
        <Field
          label={t('account.passwordConfirm')}
          error={errors.confirmPassword}
          kind="passwordConfirm"
        >
          <Input
            type="password"
            autoComplete="new-password"
            {...form.register('confirmPassword')}
          />
        </Field>
        <Button type="submit" variant="secondary" disabled={change.isPending}>
          {t('account.passwordSubmit')}
        </Button>
        <p className="text-xs text-muted">{t('account.passwordHint')}</p>
      </form>
    </Card>
  );
}

function DeleteAccountCard({ isOwner, familyName }: { isOwner: boolean; familyName: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm<DeleteAccountRequest>({ resolver: zodResolver(deleteAccountRequestSchema) });
  const remove = useMutation({
    mutationFn: (v: DeleteAccountRequest) =>
      api.me.deleteAccount(v.password, isOwner ? v.familyName : undefined),
    onSuccess: () => navigate('/login', { replace: true }),
  });
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>{t('account.deleteTitle')}</CardTitle>
      <p className="text-sm text-muted">
        {isOwner ? t('account.deleteIntroOwner') : t('account.deleteIntro')}
      </p>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={form.handleSubmit((v) => remove.mutate(v))}
      >
        <FormError error={remove.error} />
        <Field label={t('account.deleteConfirm')} error={form.formState.errors.password}>
          <Input type="password" autoComplete="current-password" {...form.register('password')} />
        </Field>
        {isOwner && (
          <Field label={t('account.deleteFamilyName', { name: familyName })}>
            <Input autoComplete="off" {...form.register('familyName')} />
          </Field>
        )}
        <Button type="submit" variant="danger" disabled={remove.isPending}>
          {t('account.deleteSubmit')}
        </Button>
      </form>
    </Card>
  );
}

export function AccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const logout = useMutation({
    mutationFn: (everywhere: boolean) => (everywhere ? api.auth.logoutAll() : api.auth.logout()),
    onSettled: () => navigate('/login', { replace: true }),
  });

  if (!me.data) return <p role="status">{t('app.loading')}</p>;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('account.title')} back="/family" />
      <p className="text-muted">{t('account.signedInAs', { email: me.data.user.email })}</p>
      <ThemeCard />
      <ChangePasswordCard />
      <Card className="flex flex-col gap-3">
        <CardTitle>{t('account.sessions')}</CardTitle>
        <FormError error={logout.error} />
        <Button
          variant="secondary"
          onClick={() => logout.mutate(false)}
          disabled={logout.isPending}
        >
          {t('account.logout')}
        </Button>
        <Button variant="secondary" onClick={() => logout.mutate(true)} disabled={logout.isPending}>
          {t('account.logoutAll')}
        </Button>
        <p className="text-xs text-muted">{t('account.logoutAllHint')}</p>
      </Card>
      <DeleteAccountCard
        isOwner={me.data.member.role === 'owner'}
        familyName={me.data.family.name}
      />
    </div>
  );
}
